import { ChevronRight } from "lucide-react";
import { cn } from "@/modern/utils";
import { Badge } from "@/modern/components/ui/badge";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/modern/components/ui/tooltip";
import {
  problemHubStatusBadgeClass,
  sequenceBadgeClassFromStatus,
  SEQUENCE_STATUS_LABELS,
  SEQUENCE_STATUS_TOOLTIPS,
  BUCKET_STATUS_LABELS,
  BUCKET_STATUS_TOOLTIPS,
  getHubProgress,
  getHubProgressBarClass,
  getHubProgressTextClass,
} from "../constants";
import type { BucketStatus, ProblemHub } from "../types";
import { useCoverage } from "../context/CoverageContext";

const StatusBadge = ({ status }: { status: BucketStatus }) => (
  <Tooltip>
    <TooltipTrigger asChild>
      <Badge variant="secondary" size="sm" className={cn(problemHubStatusBadgeClass(status), "cursor-default")}>
        {BUCKET_STATUS_LABELS[status]}
      </Badge>
    </TooltipTrigger>
    <TooltipContent side="bottom" align="center" sideOffset={6} className="max-w-[220px] text-left border">
      <p className="text-xs font-medium mb-0.5">Content strength — {BUCKET_STATUS_LABELS[status]}</p>
      <p className="text-xs text-muted-foreground">{BUCKET_STATUS_TOOLTIPS[status]}</p>
    </TooltipContent>
  </Tooltip>
);

const SeqBadge = ({ hub }: { hub: ProblemHub }) => (
  <Tooltip>
    <TooltipTrigger asChild>
      <Badge variant="secondary" size="sm" className={cn(sequenceBadgeClassFromStatus(hub.sequenceStatus), "cursor-default")}>
        {SEQUENCE_STATUS_LABELS[hub.sequenceStatus]}
      </Badge>
    </TooltipTrigger>
    <TooltipContent side="bottom" align="center" sideOffset={6} className="max-w-[220px] text-left border">
      <p className="text-xs font-medium mb-0.5">Sequence — {SEQUENCE_STATUS_LABELS[hub.sequenceStatus]}</p>
      <p className="text-xs text-muted-foreground">{SEQUENCE_STATUS_TOOLTIPS[hub.sequenceStatus]}</p>
    </TooltipContent>
  </Tooltip>
);

export const HubRow = ({ hub }: { hub: ProblemHub }) => {
  const { selectedHub, setSelectedHub } = useCoverage();

  const pct = getHubProgress(hub);
  const isSelected = selectedHub?.id === hub.id;

  const handleSelect = () => setSelectedHub(isSelected ? null : hub);

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      handleSelect();
    }
  };

  return (
    <div
      data-hub-id={hub.id}
      role="button"
      tabIndex={0}
      onClick={handleSelect}
      onKeyDown={handleKeyDown}
      className={cn(
        "rounded-xl overflow-hidden border border-border bg-card text-card-foreground cursor-pointer transition-colors duration-150 outline-none focus-visible:ring-2 focus-visible:ring-ring",
        isSelected ? "bg-primary/10" : "hover:bg-accent/40"
      )}
    >
      <div className="flex items-center gap-3.5 px-4 py-3.5">
        <div className="text-sm font-medium flex-1 min-w-0 truncate text-foreground">
          {hub.name}
        </div>

        <div className="flex items-center gap-1.5">
          <StatusBadge status={hub.overallStatus} />
          <SeqBadge hub={hub} />
        </div>

        <div className="w-16 h-0.5 bg-muted/30 rounded-sm overflow-hidden">
          <div
            className={cn("h-full rounded-sm transition-[width] duration-300", getHubProgressBarClass(hub))}
            style={{ width: `${pct}%` }}
          />
        </div>

        <span className={cn("font-mono text-xs min-w-9 text-right", getHubProgressTextClass(hub))}>
          {pct}%
        </span>

        <ChevronRight
          className={cn("size-3.5", isSelected ? "text-primary/65" : "text-muted-foreground/60")}
        />
      </div>
    </div>
  );
};
